import { calculateInvoice, chargedRate, defaultVatCode, formatQuantity, VAT_CODE_GROUP_LABELS, VAT_CODES, vatCode, type InvoiceKind, type InvoiceLine, type InvoiceTotals, type VatCodeGroup } from '@fsp/core';
import { useMemo, type Dispatch, type ReactNode, type SetStateAction } from 'react';
import { basisPoints, cents, milli, money, moneyInput, quantityInput, rateInput } from '../form';
import { useI18n } from '../i18n';
import { Alert } from '../layout';

/** One editable invoice line as typed (text), shared by the issued and received invoice forms. */
export interface LineDraft { kind: 'item' | 'discount'; description: string; quantity: string; unit: string; unitPrice: string; vatRate: string; /** VAT classifier code; empty = the domestic code of the rate. */ vatCode: string; mode: 'percent' | 'fixed'; value: string }

/** A line in API units (cents, thousandths, basis points), as the API returns and accepts it. */
export interface ApiLine { kind: 'item' | 'discount'; description: string; quantity?: number; unit?: string; unitPrice?: number; vatRate?: number; vatCode?: string | null; discountMode?: 'percent' | 'fixed' | null; discountValue?: number | null; mode?: 'percent' | 'fixed'; value?: number }

export const emptyItem = (vatRate = '21', vatCode = ''): LineDraft => ({ kind: 'item', description: '', quantity: '1', unit: 'vnt.', unitPrice: '', vatRate, vatCode, mode: 'percent', value: '' });
export const emptyDiscount = (): LineDraft => ({ kind: 'discount', description: 'Nuolaida', quantity: '1', unit: '%', unitPrice: '', vatRate: '', vatCode: '', mode: 'percent', value: '' });

export function draftFromLine(l: ApiLine): LineDraft {
  if (l.kind === 'item') {
    return { kind: 'item', description: l.description, quantity: quantityInput(l.quantity ?? 1000), unit: l.unit ?? 'vnt.', unitPrice: moneyInput(l.unitPrice ?? 0), vatRate: rateInput(l.vatRate ?? 0), vatCode: l.vatCode ?? '', mode: 'percent', value: '' };
  }
  const mode = l.discountMode ?? l.mode ?? 'percent';
  const value = l.discountValue ?? l.value ?? 0;
  return { kind: 'discount', description: l.description, quantity: '1', unit: mode === 'percent' ? '%' : '€', unitPrice: '', vatRate: l.vatRate ? rateInput(l.vatRate) : '', vatCode: l.vatCode ?? '', mode, value: mode === 'fixed' ? moneyInput(value) : rateInput(value) };
}

/** Typed lines → API/core units; null when a number cannot be read. */
export function toLines(drafts: LineDraft[], vatPayer: boolean): InvoiceLine[] | null {
  try {
    return drafts.map((d) => d.kind === 'item'
      ? { kind: 'item', description: d.description || '—', quantity: milli(d.quantity || '1'), unit: d.unit || 'vnt.', unitPrice: cents(d.unitPrice), vatRate: vatPayer ? basisPoints(d.vatRate) : 0, ...(vatPayer && d.vatCode ? { vatCode: d.vatCode } : {}) }
      : { kind: 'discount', description: d.description || 'Nuolaida', mode: d.mode, value: d.mode === 'percent' ? basisPoints(d.value) : cents(d.value), ...(vatPayer && d.vatRate ? { vatRate: basisPoints(d.vatRate) } : {}) });
  } catch {
    return null;
  }
}

/** Live totals with the same @fsp/core arithmetic the API uses; a message when the lines do not add up. */
export function useTotals(lines: LineDraft[], kind: InvoiceKind, vatPayer: boolean): { core: InvoiceLine[] | null; totals: InvoiceTotals | string } {
  const { t } = useI18n();
  return useMemo(() => {
    const core = toLines(lines, vatPayer);
    if (!core) return { core, totals: t('Patikrinkite sumas ir kiekius.') };
    try { return { core, totals: calculateInvoice(core, { kind, sellerIsVatPayer: vatPayer }) }; } catch (e) { return { core, totals: (e as Error).message }; }
  }, [lines, kind, vatPayer, t]);
}

const RATES = ['21', '12', '9', '5', '0'];
const GROUP_ORDER: VatCodeGroup[] = ['domestic', 'zero', 'exempt', 'reverse', 'eu', 'foreign', 'margin', 'private', 'assets', 'other'];

export function LinesEditor({ lines, setLines, totals, vatPayer, classify = vatPayer, extraHeader, printed, register = 'issued', currency = 'EUR' }: {
  lines: LineDraft[]; setLines: Dispatch<SetStateAction<LineDraft[]>>; totals: InvoiceTotals | string; vatPayer: boolean;
  /**
   * The VAT classifier code for each line (for i.SAF and the VAT return): only for a company that is a VAT payer.
   * A received invoice of a company that is not one still carries the supplier's VAT rate (it is a cost), as a plain rate.
   */
  classify?: boolean;
  /** The invoice's currency (amounts are shown in it). */
  currency?: string;
  /** Which VAT classifier codes fit: issued invoices (sales) or received ones (purchases). */
  register?: 'issued' | 'received';
  extraHeader?: ReactNode;
  /** Total printed on an uploaded document, to compare with ours. */
  printed?: number | undefined;
}) {
  const moneyIn = (v: number) => money(v, currency);
  const { t, locale } = useI18n();
  const lang = locale === 'lt' ? 'lt' : 'en';
  const setLine = (index: number, patch: Partial<LineDraft>) => setLines((all) => all.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  const remove = (index: number) => setLines((all) => all.filter((_, j) => j !== index));
  const net = (index: number) => (typeof totals === 'string' ? '—' : moneyIn(totals.lines.find((x) => x.index === index)?.net ?? 0));
  const rateSelect = (value: string, onChange: (v: string) => void, allowEmpty = false) => (
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      {allowEmpty ? <option value="">—</option> : null}
      {(RATES.includes(value) || !value ? RATES : [value, ...RATES]).map((r) => <option key={r} value={r}>{r} %</option>)}
    </select>
  );
  // The line's VAT: the classifier code (it decides the rate charged), grouped as VMI lists them.
  const vatSelect = (l: LineDraft, onChange: (code: string) => void) => {
    const current = l.vatCode || defaultVatCode(Math.round(Number(l.vatRate.replace(',', '.')) * 100)) || '';
    return (
      <select value={current} onChange={(e) => onChange(e.target.value)} className={current ? undefined : 'is-missing'}>
        {!current ? <option value="">{t('0 % — pasirinkite PVM klasifikaciją')}</option> : null}
        {GROUP_ORDER.map((group) => {
          const codes = VAT_CODES.filter((v) => v.group === group && v[register]);
          return codes.length ? <optgroup key={group} label={VAT_CODE_GROUP_LABELS[group][lang]}>{codes.map((v) => <option key={v.code} value={v.code}>{v.code} · {v[lang]}</option>)}</optgroup> : null;
        })}
      </select>
    );
  };
  const groupLabel = (g: { rate: number; code?: string; net: number }) => {
    const v = vatCode(g.code);
    return v && !v.charged ? `${v[lang]} · ${moneyIn(g.net)}` : t('PVM {rate} % nuo {net}', { rate: formatQuantity(g.rate * 10), net: moneyIn(g.net) });
  };
  return (
    <section className="panel-card">
      <div className="card-head"><div><p className="eyebrow">{t('Eilutės')}</p><h2>{t('Prekės ir paslaugos')}</h2></div>{extraHeader}</div>
      <div className="item-list">
        {lines.map((l, i) => l.kind === 'item' ? (
          <div className={vatPayer ? 'item-row item-row--vat' : 'item-row'} key={i}>
            <label>{t('Aprašymas')}<input value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} required /></label>
            <label>{t('Kiekis')}<input inputMode="decimal" value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} /></label>
            <label>{t('Vnt.')}<input value={l.unit} onChange={(e) => setLine(i, { unit: e.target.value })} /></label>
            <label>{vatPayer ? t('Kaina be PVM (€)') : t('Kaina (€)')}<input inputMode="decimal" value={l.unitPrice} onChange={(e) => setLine(i, { unitPrice: e.target.value })} required /></label>
            {vatPayer ? (classify
              ? <label>{t('PVM')}{vatSelect(l, (code) => setLine(i, { vatCode: code, vatRate: rateInput(chargedRate(code, 0)) }))}</label>
              : <label>{t('PVM %')}{rateSelect(l.vatRate, (v) => setLine(i, { vatRate: v, vatCode: '' }))}</label>) : null}
            <strong className="line-total">{net(i)}</strong>
            <button type="button" className="icon-button" aria-label={t('Pašalinti')} onClick={() => remove(i)}>×</button>
          </div>
        ) : (
          <div className="item-row discount-row" key={i}>
            <label>{t('Nuolaidos pavadinimas')}<input value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} required /></label>
            <label>{t('Tipas')}<select value={l.mode} onChange={(e) => setLine(i, { mode: e.target.value as 'percent' | 'fixed' })}><option value="percent">%</option><option value="fixed">€</option></select></label>
            <label>{l.mode === 'percent' ? t('Nuolaida %') : t('Nuolaida (€)')}<input inputMode="decimal" value={l.value} onChange={(e) => setLine(i, { value: e.target.value })} required /></label>
            {vatPayer && l.mode === 'fixed' ? <label>{t('PVM %')}{rateSelect(l.vatRate, (v) => setLine(i, { vatRate: v }), true)}</label> : null}
            <strong className="line-total">{net(i)}</strong>
            <button type="button" className="icon-button" aria-label={t('Pašalinti')} onClick={() => remove(i)}>×</button>
          </div>
        ))}
      </div>
      <div className="form-actions">
        <button type="button" className="button button--ghost" onClick={() => setLines((all) => [...all, emptyItem(vatPayer ? '21' : '0')])}>+ {t('Pridėti eilutę')}</button>
        <button type="button" className="button button--ghost" onClick={() => setLines((all) => [...all, emptyDiscount()])}>+ {t('Nuolaida')}</button>
      </div>
      {typeof totals === 'string' ? <Alert kind="error">{totals}</Alert> : (
        <div className="invoice-totals">
          <div className="invoice-total"><span>{t('Tarpinė suma')}</span><span>{moneyIn(totals.subtotal)}</span></div>
          {totals.discountTotal ? <div className="invoice-total"><span>{t('Nuolaidos')}</span><span>−{moneyIn(totals.discountTotal)}</span></div> : null}
          {totals.vatGroups.map((g) => <div className="invoice-total" key={`${g.rate}|${g.code ?? ''}`}><span>{groupLabel(g)}</span><span>{moneyIn(g.vat)}</span></div>)}
          <div className="invoice-total"><span>{t('Iš viso')}</span><strong>{moneyIn(totals.total)}</strong></div>
          {printed !== undefined && printed !== totals.total ? <Alert kind="error">{t('Dokumente nurodyta suma {printed} skiriasi nuo apskaičiuotos. Patikrinkite eilutes.', { printed: moneyIn(printed) })}</Alert> : null}
        </div>
      )}
    </section>
  );
}
